import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Modal } from 'react-bootstrap';
import { KeyRound, Move, Plus } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { PageLoader } from '../../Components/LoadingSpinner';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import LevelNavigator from '../../Components/LevelNavigator';
import BlueprintWithMarkers, { NumberedMarker } from '../../Components/BlueprintWithMarkers';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useStorageUnits } from '../../hooks/storageUnits';
import { useBuildingLeases } from '../../hooks/leases';
import { endedLeasesForObject, leasesPath } from '../../Helpers/leaseUtils';
import { LeaseObjectType } from '../../types/Enums';
import { useTenants } from '../../hooks/tenants';
import { BlueprintLocation, PhotoModel } from '../../types/protocols';
import { StorageUnitInput } from '../../types/storageUnits';
import { uploadLeaseContract } from '../../Helpers/leaseContractUtils';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { nextNumberInSeries } from '../../Helpers/numberSeries';
import {
    STORAGE_UNIT_ICON,
    storageUnitStatusColor,
    defaultStorageLevelKey,
    isUnitRented,
    storageLevelsDescending,
    storageUnitDisplayName,
    totalUnitArea,
    unitsOnLevel,
    vacantUnitCount,
} from '../../Helpers/storageUnitUtils';
import LeaseModal, { LeaseModalSubmitData } from '../Leases/LeaseModal';
import StorageUnitModal from './StorageUnitModal';
import StorageUnitList from './StorageUnitList';
import StorageUnitDetailModal from './StorageUnitDetailModal';
import './StorageUnits.css';

/**
 * Drawing interaction modes, each started and ended explicitly (as on the parking page):
 * - new:   click the drawing to create units in series
 * - place: click the drawing to place one (unplaced) unit
 * - move:  drag existing markers to new positions ("Flytta markörer")
 */
type PlacementMode = { kind: 'none' } | { kind: 'new' } | { kind: 'place'; unitKey: string } | { kind: 'move' };

const formatArea = (area: number): string => `${area.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} m²`;

/**
 * A building's storage units (förråd): /building/:id/storage-units. One page for
 * the whole building with a level selector; the level's current blueprint is
 * the drawing (plan D4, D5). `?level=` deep-links a level, `?unit=` opens a unit
 * selected (read once on load, as the drawings on BuildingPage link it).
 */
const StorageUnitsPage: React.FC = () => {
    const { id: buildingKey } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const isAdmin = useIsAdmin();
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const storage = useStorageUnits({ buildingKey });
    const { units, sortedUnits, loading: loadingUnits, error, submitting, clearError, setUnitLocation, refresh: refreshUnits } = storage;
    const leaseHook = useBuildingLeases({ buildingKey, onMutated: refreshUnits });
    const { getTenantName } = useTenants();

    // The highlighted unit (list row + marker). Placement and move mode set it
    // too; only a click opens the detail modal (`unitModalOpen`).
    const [selectedUnitKey, setSelectedUnitKey] = useState<string | null>(() => searchParams.get('unit'));
    const [unitModalOpen, setUnitModalOpen] = useState(() => searchParams.has('unit'));
    const [placement, setPlacement] = useState<PlacementMode>({ kind: 'none' });
    const [pendingLocation, setPendingLocation] = useState<BlueprintLocation | undefined>(undefined);
    const [lastCreatedUnitKey, setLastCreatedUnitKey] = useState<string | null>(null);
    const [showUnitModal, setShowUnitModal] = useState<'closed' | 'new' | 'edit'>('closed');
    const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);
    const [vacantOnly, setVacantOnly] = useState(false);
    const [showLeaseModal, setShowLeaseModal] = useState(false);

    useEffect(() => {
        if (buildingKey && (!currentBuilding || currentBuilding.key !== buildingKey)) {
            fetchBuilding(buildingKey);
        }
    }, [buildingKey, currentBuilding, fetchBuilding]);

    const buildingName = currentBuilding?.summary?.name || 'Byggnad';
    useDocumentTitle(`Förråd - ${buildingName}`);

    const levelsMap = useMemo(() => currentBuilding?.model.structure?.levels || {}, [currentBuilding]);
    const levels = useMemo(() => storageLevelsDescending(levelsMap), [levelsMap]);

    // Level selection: ?level= if valid, else the D5 default. Kept in the URL so it survives reloads.
    const requestedLevelKey = searchParams.get('level');
    const selectedLevelKey = useMemo(() => {
        if (requestedLevelKey && levels.some(l => l.key === requestedLevelKey)) return requestedLevelKey;
        return defaultStorageLevelKey(levels, sortedUnits);
    }, [requestedLevelKey, levels, sortedUnits]);
    const selectLevel = useCallback((levelKey: string) => {
        setSearchParams(levelKey ? { level: levelKey } : {}, { replace: true });
        setSelectedUnitKey(null);
        setPlacement({ kind: 'none' });
        setPendingLocation(undefined);
    }, [setSearchParams]);

    const level = selectedLevelKey ? levelsMap[selectedLevelKey] : undefined;
    const currentBlueprintKey = level?.currentBlueprintKey;
    const currentBlueprint = level && currentBlueprintKey ? level.blueprints?.[currentBlueprintKey] : undefined;
    const hasDrawing = !!(currentBlueprint && currentBlueprintKey);

    const levelUnits = useMemo(() => (selectedLevelKey ? unitsOnLevel(sortedUnits, selectedLevelKey) : []), [sortedUnits, selectedLevelKey]);
    const selectedUnit = selectedUnitKey ? units[selectedUnitKey] : undefined;
    const selectedLease = selectedUnit?.currentLeaseKey
        ? leaseHook.leases.find(l => l.key === selectedUnit.currentLeaseKey)
        : undefined;
    // Closes when the unit leaves the selection (level switch, a ?unit= key that is not in the building), so a
    // unit selected later by placement does not open it.
    useEffect(() => {
        if (!loadingUnits && unitModalOpen && !selectedUnit) setUnitModalOpen(false);
    }, [loadingUnits, unitModalOpen, selectedUnit]);
    const selectedEndedLeases = isAdmin && selectedUnit
        ? endedLeasesForObject(leaseHook.leases, { type: LeaseObjectType.StorageUnit, key: selectedUnit.key })
        : undefined;

    // "Lediga" filter applies to both the list and the drawing; counts use all units.
    const visibleUnits = useMemo(() => (vacantOnly ? levelUnits.filter(unit => !isUnitRented(unit)) : levelUnits), [levelUnits, vacantOnly]);

    const unitMarkers = useMemo((): NumberedMarker[] => visibleUnits.map(unit => ({
        key: unit.key,
        number: unit.number,
        color: storageUnitStatusColor(unit),
        icon: STORAGE_UNIT_ICON,
        iconColored: true,
        isRented: isUnitRented(unit),
        displayName: storageUnitDisplayName(unit),
        blueprintLocations: unit.blueprintLocations,
    })), [visibleUnits]);

    // Series defaults: the last created unit, else the highest-numbered unit on the level (R4).
    const seriesSource = useMemo(() => {
        const last = lastCreatedUnitKey ? units[lastCreatedUnitKey] : undefined;
        if (last && last.levelKey === selectedLevelKey) return last;
        return levelUnits[levelUnits.length - 1];
    }, [lastCreatedUnitKey, units, levelUnits, selectedLevelKey]);

    // Memoised so re-renders while the modal is open do not reset the form.
    const newUnitInitialValues = useMemo((): Partial<StorageUnitInput> | undefined => {
        if (showUnitModal !== 'new') return undefined;
        return {
            number: seriesSource ? (nextNumberInSeries(seriesSource.number) ?? '') : '',
            type: seriesSource?.type,
            area: seriesSource?.area,
            ceilingHeight: seriesSource?.ceilingHeight,
            levelKey: selectedLevelKey ?? undefined,
        };
    }, [showUnitModal, seriesSource, selectedLevelKey]);

    const exitPlacement = useCallback(() => {
        setPlacement({ kind: 'none' });
        setPendingLocation(undefined);
    }, []);

    const handleBlueprintClick = useCallback(async (location: BlueprintLocation) => {
        if (!currentBlueprintKey) return;
        if (placement.kind === 'place') {
            const unitKey = placement.unitKey;
            exitPlacement();
            await setUnitLocation(unitKey, currentBlueprintKey, location).catch(() => undefined);
            setSelectedUnitKey(unitKey);
            return;
        }
        if (placement.kind === 'new') {
            setPendingLocation(location);
            setShowUnitModal('new');
        }
    }, [currentBlueprintKey, placement, setUnitLocation, exitPlacement]);

    // A click in the list or on a marker; in placement or move mode it only highlights.
    const handleUnitClick = useCallback((unitKey: string) => {
        setSelectedUnitKey(unitKey);
        if (placement.kind === 'none') setUnitModalOpen(true);
    }, [placement.kind]);

    // Move mode: a dropped marker is written immediately; the mode stays on until "Klar".
    const handleUnitMove = useCallback((unitKey: string, location: BlueprintLocation) => {
        if (!currentBlueprintKey) return;
        setSelectedUnitKey(unitKey);
        setUnitLocation(unitKey, currentBlueprintKey, location).catch(() => undefined);
    }, [currentBlueprintKey, setUnitLocation]);

    if (loadingBuilding && !currentBuilding) return <PageLoader />;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding || !buildingKey) return <p>Byggnaden hittades ej</p>;

    const handleUnitSubmit = async (input: StorageUnitInput) => {
        if (showUnitModal === 'edit' && selectedUnit) {
            await storage.updateUnit(selectedUnit.key, input);
            // A unit moved to another level leaves the list; follow it so the modal keeps showing it.
            if (input.levelKey !== selectedLevelKey) {
                selectLevel(input.levelKey);
                setSelectedUnitKey(selectedUnit.key);
            }
            return;
        }
        // A location only makes sense on the level being shown; a unit created for another level is left unplaced.
        const location = input.levelKey === selectedLevelKey ? pendingLocation : undefined;
        const unit = await storage.createUnit(input, location);
        setLastCreatedUnitKey(unit.key);
        setSelectedUnitKey(unit.key);
        setPendingLocation(undefined);
        // Series placement: stay in placement mode until the user stops it.
    };

    const handleUnitModalHide = () => {
        setShowUnitModal('closed');
        setPendingLocation(undefined);
    };

    const handleConfirmDelete = async () => {
        if (!confirmDeleteKey) return;
        const unitKey = confirmDeleteKey;
        setConfirmDeleteKey(null);
        // Closed before the write so the detail modal does not come back for a unit being deleted.
        setUnitModalOpen(false);
        try {
            await storage.deleteUnit(unitKey);
            if (selectedUnitKey === unitKey) setSelectedUnitKey(null);
        } catch {
            // Error is surfaced through the hook's error state.
        }
    };

    const handleLeaseSubmit = async ({ input, filesToUpload }: LeaseModalSubmitData) => {
        const lease = await leaseHook.createLease(input);
        const user = getAuth().currentUser;
        if (user) {
            for (const file of filesToUpload) {
                const result = await uploadLeaseContract(lease.key, file, user.uid);
                if (result.success && result.attachment) {
                    await leaseHook.addContract(lease.key, result.attachment);
                } else if (result.error) {
                    throw new Error(result.error);
                }
            }
        }
        setShowLeaseModal(false);
    };

    const handleDeletePhoto = async (photo: PhotoModel) => {
        if (!selectedUnit) return;
        await storage.deleteUnitPhoto(selectedUnit.key, photo.key);
    };

    // Opened from the unit modal, which steps aside while they are open and returns after.
    const unitChildModalOpen = showUnitModal === 'edit' || showLeaseModal || confirmDeleteKey !== null;

    const totalCount = sortedUnits.length;
    const levelVacant = vacantUnitCount(levelUnits);
    const levelArea = totalUnitArea(levelUnits);
    const placedOnDrawing = !!currentBlueprintKey && levelUnits.some(u => u.blueprintLocations?.[currentBlueprintKey]);

    return (
        <>
            <BreadcrumbComponent
                items={[
                    { text: buildingName, link: `/building/${buildingKey}` },
                    { text: 'Förråd' },
                ]}
            />

            <div className="storage-page">
                <div className="storage-title-container">
                    <h3>Förråd</h3>
                    <span className="storage-badge">{totalCount} förråd i byggnaden</span>
                </div>

                {error && <Alert variant="danger" dismissible onClose={clearError}>{error}</Alert>}
                {leaseHook.error && <Alert variant="danger" dismissible onClose={leaseHook.clearError}>{leaseHook.error}</Alert>}

                {levels.length === 0 ? (
                    <div className="dropshadow storage-card" style={{ display: 'block' }}>
                        <p style={{ color: '#999', margin: 0 }}>
                            Inga våningsplan. Förråd hör till en våning och placeras på dess ritning — lägg till våningsplan under Hantera våningsplan i appen först.
                        </p>
                    </div>
                ) : (
                    <>
                        <LevelNavigator
                            className="mb-3"
                            levels={levels}
                            selectedKey={selectedLevelKey ?? undefined}
                            onSelect={selectLevel}
                            getCount={key => unitsOnLevel(sortedUnits, key).length}
                        />

                        <div className="dropshadow storage-card">
                            <div className="storage-drawing-column">
                                <div className="storage-section-header">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h4>{level ? levelDisplayName(level) : 'Ritning'}</h4>
                                        <span className="storage-badge">{levelUnits.length} förråd</span>
                                        <span className={`storage-badge ${levelVacant > 0 ? 'storage-badge--success' : 'storage-badge--muted'}`}>Lediga: {levelVacant}</span>
                                        {levelArea > 0 && <span className="storage-badge">Totalt {formatArea(levelArea)}</span>}
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            active={vacantOnly}
                                            onClick={() => setVacantOnly(v => !v)}
                                            title="Visa bara lediga förråd"
                                        >
                                            <KeyRound size={14} className="me-1" />Lediga
                                        </Button>
                                        {isAdmin && hasDrawing && placement.kind === 'none' && (
                                            <>
                                                <Button variant="primary" size="sm" onClick={() => setPlacement({ kind: 'new' })} disabled={submitting}>
                                                    <Plus size={14} className="me-1" />Placera nya förråd
                                                </Button>
                                                {placedOnDrawing && (
                                                    <Button variant="outline-primary" size="sm" onClick={() => setPlacement({ kind: 'move' })} disabled={submitting}>
                                                        <Move size={14} className="me-1" />Flytta markörer
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {placement.kind !== 'none' && (
                                    <div className={`storage-placement-banner ${placement.kind === 'move' ? 'storage-placement-banner--move' : ''}`}>
                                        <span>
                                            {placement.kind === 'new' && 'Klicka på ritningen där nästa förråd ligger. Numret föreslås automatiskt.'}
                                            {placement.kind === 'place' && `Klicka på ritningen där ${selectedUnit ? storageUnitDisplayName(selectedUnit).toLowerCase() : 'förrådet'} ska ligga.`}
                                            {placement.kind === 'move' && (
                                                <><strong>Flyttläge:</strong> dra en markör till rätt plats. Varje släpp sparas direkt. Avsluta med Klar.</>
                                            )}
                                        </span>
                                        <Button variant={placement.kind === 'place' ? 'outline-secondary' : 'primary'} size="sm" onClick={exitPlacement}>
                                            {placement.kind === 'place' ? 'Avbryt' : 'Klar'}
                                        </Button>
                                    </div>
                                )}

                                {hasDrawing && currentBlueprint && currentBlueprintKey ? (
                                    <BlueprintWithMarkers
                                        blueprint={currentBlueprint}
                                        currentBlueprintKey={currentBlueprintKey}
                                        building={currentBuilding}
                                        spots={unitMarkers}
                                        selectedSpotKey={selectedUnitKey}
                                        onSpotClick={handleUnitClick}
                                        placementMode={placement.kind === 'new' || placement.kind === 'place'}
                                        onBlueprintClick={handleBlueprintClick}
                                        onSpotMove={placement.kind === 'move' && isAdmin ? handleUnitMove : undefined}
                                        title=""
                                    />
                                ) : (
                                    <div className="storage-blueprint-placeholder">
                                        <span>Ingen ritning uppladdad för denna våning.</span>
                                        {level && (
                                            <small>
                                                Förråd kan skapas i listan och placeras när våningen har en ritning.{' '}
                                                <Link to={`/building/${buildingKey}/level/${level.key}`}>Ladda upp ritning på våningssidan</Link>
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="storage-side-column">
                                <div>
                                    <div className="storage-section-header">
                                        <h4>Förråd</h4>
                                        {isAdmin && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => { setPendingLocation(undefined); setShowUnitModal('new'); }}
                                                disabled={submitting}
                                            >
                                                <Plus size={14} className="me-1" />Nytt förråd
                                            </Button>
                                        )}
                                    </div>
                                    <StorageUnitList
                                        units={visibleUnits}
                                        selectedUnitKey={selectedUnitKey}
                                        currentBlueprintKey={hasDrawing ? currentBlueprintKey : undefined}
                                        onSelect={handleUnitClick}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Mounted while open; hidden (state kept) while one of its child modals is open. */}
            {unitModalOpen && selectedUnit && (
                <StorageUnitDetailModal
                    key={selectedUnit.key}
                    show={!unitChildModalOpen}
                    onHide={() => setUnitModalOpen(false)}
                    building={currentBuilding}
                    unit={selectedUnit}
                    level={levelsMap[selectedUnit.levelKey]}
                    isAdmin={isAdmin}
                    submitting={submitting}
                    hasDrawing={hasDrawing && selectedUnit.levelKey === selectedLevelKey}
                    isPlacedOnCurrentDrawing={!!(currentBlueprintKey && selectedUnit.blueprintLocations?.[currentBlueprintKey])}
                    lease={selectedLease}
                    tenantName={selectedLease ? getTenantName(selectedLease.tenantKey) : undefined}
                    endedLeases={selectedEndedLeases}
                    getTenantName={getTenantName}
                    leasesLink={isAdmin && buildingKey ? leasesPath(buildingKey, selectedUnit.currentLeaseKey) : undefined}
                    onNewLease={() => setShowLeaseModal(true)}
                    onEdit={() => setShowUnitModal('edit')}
                    onDelete={() => setConfirmDeleteKey(selectedUnit.key)}
                    onStartPlace={() => {
                        setUnitModalOpen(false);
                        setPlacement({ kind: 'place', unitKey: selectedUnit.key });
                    }}
                />
            )}

            <StorageUnitModal
                show={showUnitModal !== 'closed'}
                onHide={handleUnitModalHide}
                onSubmit={handleUnitSubmit}
                levels={levelsMap}
                allUnits={sortedUnits}
                unit={showUnitModal === 'edit' ? selectedUnit : undefined}
                photoActions={showUnitModal === 'edit' && selectedUnit ? {
                    photos: selectedUnit.photos || {},
                    buildingKey: currentBuilding.key,
                    onDeletePhoto: handleDeletePhoto,
                    onUploadPhotos: uploader => storage.uploadUnitPhotos(selectedUnit.key, uploader),
                } : undefined}
                initialValues={newUnitInitialValues}
                placementLevelKey={showUnitModal === 'new' && pendingLocation && selectedLevelKey ? selectedLevelKey : undefined}
                submitting={submitting}
                onRemoveLocation={showUnitModal === 'edit' && selectedUnit && currentBlueprintKey && selectedUnit.blueprintLocations?.[currentBlueprintKey]
                    ? () => storage.setUnitLocation(selectedUnit.key, currentBlueprintKey, null)
                    : undefined}
            />

            {showLeaseModal && selectedUnit && (
                <LeaseModal
                    show={showLeaseModal}
                    onHide={() => setShowLeaseModal(false)}
                    onSubmit={handleLeaseSubmit}
                    buildingKey={buildingKey}
                    areas={[]}
                    units={sortedUnits}
                    levels={levelsMap}
                    buildingLeases={leaseHook.leases}
                    lockedUnit={{ unitKey: selectedUnit.key }}
                    submitting={leaseHook.submitting}
                />
            )}

            <Modal show={confirmDeleteKey !== null} onHide={() => setConfirmDeleteKey(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Är du säker?</Modal.Title>
                </Modal.Header>
                <Modal.Body>Förrådet och dess foton tas bort. Detta kan inte ångras.</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmDeleteKey(null)}>Avbryt</Button>
                    <Button variant="danger" onClick={handleConfirmDelete}>Ta bort</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default StorageUnitsPage;
